import {
  type AuctionRound,
  CUBE_VALUES,
  DEFAULT_STARTING_GARNETS,
  type InitGameOptions,
  MAX_PLAYER_COUNT,
  MIN_PLAYER_COUNT,
  type MinusAuctionPlayer,
  type MinusAuctionState,
} from "./types";
import { getDefaultSeed, shuffle } from "./utils";

export function getDefaultStartingChips(playerCount: number): number {
  if (playerCount >= 3 && playerCount <= 5) {
    return 11;
  }
  if (playerCount === 6) {
    return 9;
  }
  if (playerCount === 7 || playerCount === 8) {
    return 7;
  }
  if (playerCount === 9 || playerCount === 10) {
    return 5;
  }
  throw new Error("Minus Auction supports 3 to 10 players");
}

export function initGame(options: InitGameOptions): MinusAuctionState {
  assertValidInitOptions(options);

  const seed = options.seed ?? getDefaultSeed(options.playerIds);
  const shuffledCubes = shuffle(CUBE_VALUES, seed);
  const removedHiddenCube = shuffledCubes[0];
  const secondHiddenCube = shuffledCubes[1];
  const firstAuctionCube = shuffledCubes[2];

  if (
    removedHiddenCube === undefined ||
    secondHiddenCube === undefined ||
    firstAuctionCube === undefined
  ) {
    throw new Error("Minus Auction tile set is invalid");
  }

  const deck = shuffledCubes.slice(3);
  const startingChips =
    options.startingChips ?? getDefaultStartingChips(options.playerIds.length);
  const startingGarnets = options.startingGarnets ?? DEFAULT_STARTING_GARNETS;
  const players: readonly MinusAuctionPlayer[] = options.playerIds.map(
    (playerId) => ({
      playerId,
      chips: startingChips,
      garnets: startingGarnets,
      cubes: [],
    }),
  );
  const firstPlayerId = options.playerIds[0];

  if (firstPlayerId === undefined) {
    throw new Error("At least one player is required");
  }

  const auction: AuctionRound = {
    currentCube: firstAuctionCube,
    pileChips: 0,
    pileGarnets: 0,
    startPlayerId: firstPlayerId,
    currentPlayerId: firstPlayerId,
  };

  return {
    gameId: options.gameId ?? `minus-auction-${options.playerIds.join("-")}`,
    status: "IN_PROGRESS",
    phase: "AUCTION_DECISION",
    playerOrder: options.playerIds,
    players,
    hostPlayerId: options.hostPlayerId ?? firstPlayerId,
    seed,
    deck,
    removedHiddenCube,
    secondHiddenCube,
    secondHiddenCubeIncluded: false,
    hiddenAuctionResolved: false,
    acceptedCubeCount: 0,
    auction,
    hiddenAuction: null,
    transferLog: [],
    auctionLog: [
      {
        message: `첫 타일 ${firstAuctionCube} 공개`,
        cube: firstAuctionCube,
      },
    ],
    scores: [],
    winnerIds: [],
    eliminationCandidateIds: [],
  };
}

function assertValidInitOptions(options: InitGameOptions): void {
  if (
    options.playerIds.length < MIN_PLAYER_COUNT ||
    options.playerIds.length > MAX_PLAYER_COUNT
  ) {
    throw new Error("Minus Auction supports 3 to 10 players");
  }

  const deduped = new Set(options.playerIds);
  if (deduped.size !== options.playerIds.length) {
    throw new Error("Player IDs must be unique");
  }

  if (options.hostPlayerId && !deduped.has(options.hostPlayerId)) {
    throw new Error("Host player must be included in player IDs");
  }

  if (options.startingChips !== undefined && options.startingChips < 0) {
    throw new Error("Starting chips cannot be negative");
  }

  if (options.startingGarnets !== undefined && options.startingGarnets < 0) {
    throw new Error("Starting garnets cannot be negative");
  }
}
